import {useCallback} from 'react';
import {excludeQueryParams} from './excludeQueryParams';
import {useHistory} from './useHistory';
import {useSearch} from './useLocation';
import {
  URLSearchParamsWithUtm,
  URLSearchParamsWithoutUtm,
  UTM_PARAMS,
} from './utmParams';

interface Options {
  includeUtmParams: boolean;
}

const createParams = (search: string, options?: Options) => {
  let queryParams = new URLSearchParams(search);
  if (!options?.includeUtmParams && queryParams.size !== 0) {
    queryParams = excludeQueryParams(queryParams, UTM_PARAMS);
  }

  return queryParams;
};

/**
 * Returns a `URLSearchParams` object containing all parsed URL query parameters with the corresponding values,
 * except for UTM and other SEO parameters which are excluded by default.
 *
 * To also include UTM and related SEO parameters, provide the options param as `{includeUtmParams: true}`.
 */
export function useQueryParams(options: {
  includeUtmParams: true;
}): URLSearchParamsWithUtm;
export function useQueryParams(options?: {
  includeUtmParams: false;
}): URLSearchParamsWithoutUtm;
export function useQueryParams(options?: Options) {
  const search = useSearch();

  return createParams(search, options);
}

/**
 * Returns a function that returns `URLSearchParams` object containing all parsed URL query parameters with the corresponding values,
 * except for UTM and other SEO parameters which are excluded by default.
 *
 * To also include UTM and related SEO parameters, provide the options param as `{includeUtmParams: true}`.
 */
export function useQueryParamsCallback() {
  const history = useHistory();

  return useCallback(
    ((options?: Options) =>
      createParams(history.location.search, options)) as typeof useQueryParams,
    [history],
  );
}
